'use client';
import React, { useState, useRef } from 'react';

const demoVehicles: Record<string, Record<string, string>> = {
  'porsche cayenne': { motor: '2.9L Biturbo V6', potencia: '434 hp', combustible: 'Gasolina', ano: '2024', asientos: '5', peso: '2,195 kg', velocidad: '286 km/h', traccion: 'AWD', descripcion: 'El Cayenne S combina prestaciones deportivas únicas con el confort de un SUV de lujo. Aceleración de 0-100 km/h en 5.2 segundos con el refinamiento característico de Porsche.' },
  'bmw x5': { motor: '3.0L TwinPower Turbo', potencia: '335 hp', combustible: 'Gasolina', ano: '2023', asientos: '5', peso: '2,095 kg', velocidad: '250 km/h', traccion: 'xDrive AWD', descripcion: 'El BMW X5 xDrive40i ofrece potencia y elegancia alemana en su máxima expresión. Sistema de suspensión adaptativa y tecnología de conducción de última generación.' },
  'range rover': { motor: '3.0L Turbo I6 MHEV', potencia: '395 hp', combustible: 'Gasolina', ano: '2024', asientos: '5', peso: '2,235 kg', velocidad: '250 km/h', traccion: '4WD', descripcion: 'El Range Rover Sport redefine el lujo en los SUV con tecnología de suspensión neumática y capacidades todoterreno excepcionales.' },
  'can-am maverick': { motor: '900cc Turbo RR', potencia: '200 hp', combustible: 'Gasolina', ano: '2024', asientos: '2', peso: '784 kg', velocidad: '180 km/h', traccion: 'AWD', descripcion: 'El Can-Am Maverick X3 Turbo RR es el vehículo off-road más potente del mercado, con suspensión de largo recorrido y tecnología de competición.' },
  'sea-doo rxp': { motor: '1,630cc Rotax ACE', potencia: '325 hp', combustible: 'Gasolina', ano: '2024', asientos: '1', peso: '411 kg', velocidad: '110 km/h', traccion: 'Jet propulsion', descripcion: 'La Sea-Doo RXP-X 325 es la moto acuática más rápida del mundo, diseñada para rendimiento extremo con tecnología hidrodinámica exclusiva.' },
  'ram 1500': { motor: '5.7L HEMI V8', potencia: '395 hp', combustible: 'Gasolina', ano: '2024', asientos: '5', peso: '2,099 kg', velocidad: '193 km/h', traccion: '4WD', descripcion: 'La RAM 1500 Laramie establece el estándar de las pickups premium con su motor HEMI V8, interior de lujo y capacidad de remolque de 4,800 kg.' },
  'toyota hilux': { motor: '2.8L Diesel Turbo', potencia: '204 hp', combustible: 'Diésel', ano: '2024', asientos: '5', peso: '1,985 kg', velocidad: '175 km/h', traccion: '4WD', descripcion: 'La Toyota Hilux GR Sport 2024 lleva la leyenda de la Hilux al siguiente nivel con suspensión deportiva GR y equipamiento exclusivo de serie.' },
  'ford ranger': { motor: '2.0L EcoBlue Bi-Turbo', potencia: '213 hp', combustible: 'Diésel', ano: '2023', asientos: '5', peso: '1,928 kg', velocidad: '180 km/h', traccion: '4WD', descripcion: 'La Ford Ranger Wildtrak combina capacidad todoterreno con tecnología moderna y el más alto nivel de confort para uso diario exigente.' },
};

const fallback = { motor: '2.0L Turbo', potencia: '250 hp', combustible: 'Gasolina', ano: '2024', asientos: '5', peso: '1,800 kg', velocidad: '220 km/h', traccion: 'AWD', descripcion: 'Vehículo premium con tecnología de última generación y acabados de alta calidad para el conductor exigente.' };

function matchVehicle(query: string) {
  const q = query.toLowerCase();
  for (const key of Object.keys(demoVehicles)) {
    if (q.includes(key)) return demoVehicles[key];
  }
  return fallback;
}

type DemoState = 'idle' | 'loading' | 'done';

export default function DriveXAIDemo() {
  const [query, setQuery] = useState('');
  const [state, setState] = useState<DemoState>('idle');
  const [fields, setFields] = useState<Record<string, string> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearch = () => {
    if (!query.trim()) return;
    if (state === 'done') {
      setState('idle');
      setFields(null);
      setQuery('');
      return;
    }
    setState('loading');
    setTimeout(() => {
      setFields(matchVehicle(query));
      setState('done');
    }, 1600);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const fieldPairs = [
    ['motor', 'Motor', 'potencia', 'Potencia'],
    ['combustible', 'Combustible', 'ano', 'Año'],
    ['asientos', 'Asientos', 'peso', 'Peso'],
    ['velocidad', 'Velocidad máxima', 'traccion', 'Tracción'],
  ];

  return (
    <section
      id="demo"
      className="dx-font dx-grid-texture"
      style={{ background: '#1A1A1A', padding: '100px 0' }}
    >
      <div className="max-w-4xl mx-auto px-6">
        {/* Eyebrow + Headline */}
        <div className="text-center mb-10">
          <div style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '10px', color: '#C8102E', letterSpacing: '0.25em', textTransform: 'uppercase', marginBottom: '16px' }}>
            INTELIGENCIA ARTIFICIAL INCLUIDA EN PRO
          </div>
          <h2 style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 900, fontSize: 'clamp(28px, 4vw, 48px)', color: '#FFFFFF', marginBottom: '16px' }}>
            Llena las especificaciones en 3 segundos.
          </h2>
          <p style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '15px', color: '#AAAAAA', maxWidth: '520px', margin: '0 auto', lineHeight: 1.7 }}>
            Escribe el nombre de cualquier vehículo y DriveX busca automáticamente todas las especificaciones del fabricante.
          </p>
        </div>

        {/* Demo widget */}
        <div style={{ background: '#FFFFFF', borderRadius: '12px', padding: '28px', maxWidth: '640px', margin: '0 auto' }}>
          {/* Input row */}
          <div className="flex gap-3 mb-6">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe un vehículo — ej: 2024 Porsche Cayenne S"
              disabled={state === 'loading'}
              style={{
                flex: 1, fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13px',
                color: '#0A0A0A', background: '#F5F5F5', border: '1px solid #E5E5E5',
                borderRadius: '6px', padding: '12px 14px', outline: 'none',
              }}
            />
            <button
              onClick={handleSearch}
              disabled={state === 'loading'}
              style={{
                fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '12px',
                color: '#FFFFFF', background: state === 'done' ? '#10B981' : '#C8102E',
                border: 'none', borderRadius: '6px', padding: '12px 16px', cursor: 'pointer',
                whiteSpace: 'nowrap', transition: 'background 0.3s ease', minWidth: '140px',
              }}
            >
              {state === 'loading' ? '⏳ Buscando...' : state === 'done' ? '✓ Encontrado' : 'Buscar especificaciones'}
            </button>
          </div>

          {/* Fields grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            {fieldPairs.map(([k1, l1, k2, l2]) => (
              <React.Fragment key={k1}>
                <div>
                  <label style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '10px', color: '#888888', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '4px' }}>{l1}</label>
                  {state === 'loading' ? (
                    <div className="dx-skeleton" style={{ height: '36px', borderRadius: '4px' }} />
                  ) : (
                    <input
                      readOnly
                      value={fields ? fields[k1] : ''}
                      placeholder="—"
                      style={{ width: '100%', fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13px', color: '#0A0A0A', background: '#F5F5F5', border: '1px solid #E5E5E5', borderRadius: '4px', padding: '8px 10px', outline: 'none', boxSizing: 'border-box' }}
                    />
                  )}
                </div>
                <div>
                  <label style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '10px', color: '#888888', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '4px' }}>{l2}</label>
                  {state === 'loading' ? (
                    <div className="dx-skeleton" style={{ height: '36px', borderRadius: '4px' }} />
                  ) : (
                    <input
                      readOnly
                      value={fields ? fields[k2] : ''}
                      placeholder="—"
                      style={{ width: '100%', fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13px', color: '#0A0A0A', background: '#F5F5F5', border: '1px solid #E5E5E5', borderRadius: '4px', padding: '8px 10px', outline: 'none', boxSizing: 'border-box' }}
                    />
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* Description full width */}
          <div>
            <label style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '10px', color: '#888888', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '4px' }}>Descripción</label>
            {state === 'loading' ? (
              <div className="dx-skeleton" style={{ height: '72px', borderRadius: '4px' }} />
            ) : (
              <textarea
                readOnly
                value={fields ? fields['descripcion'] : ''}
                placeholder="—"
                rows={3}
                style={{ width: '100%', fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13px', color: '#0A0A0A', background: '#F5F5F5', border: '1px solid #E5E5E5', borderRadius: '4px', padding: '8px 10px', outline: 'none', resize: 'none', boxSizing: 'border-box' }}
              />
            )}
          </div>

          {/* Success banner */}
          {state === 'done' && (
            <div style={{ background: '#D1FAE5', border: '1px solid #10B981', borderRadius: '6px', padding: '10px 14px', marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#10B981', fontWeight: 700 }}>✓</span>
              <span style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '12px', color: '#065F46' }}>
                Especificaciones encontradas — todos los campos han sido completados automáticamente.
              </span>
            </div>
          )}
        </div>

        {/* Callout below */}
        <div style={{ maxWidth: '640px', margin: '16px auto 0', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
          <span style={{ fontSize: '14px' }}>🔒</span>
          <span style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '12px', color: '#AAAAAA', textAlign: 'center' }}>
            Esta función está disponible solo en el plan Pro. En el plan Starter, los campos se completan manualmente.
          </span>
        </div>
      </div>
    </section>
  );
}
